import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { 
  Plane, TrendingUp, TrendingDown, Minus, AlertTriangle, Shield, 
  BarChart3, Activity, Cpu, RefreshCw, Target, Layers, Building2, Filter, X
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import { getAircraftDropdown, getOrganizationsList } from '@/services/api';

const RISK_COLORS = {
  critical: '#ef4444',
  high: '#f97316',
  medium: '#eab308',
  low: '#22c55e',
  acceptable: '#3b82f6'
};

const SEVERITY_COLORS = {
  catastrophic: '#7c3aed',
  hazardous: '#ef4444',
  major: '#f97316',
  minor: '#eab308',
  negligible: '#22c55e'
};

export default function AssetAnalyticsPage() {
  const { t } = useTranslation();
  const { api } = useAuth();
  const { isDark } = useTheme();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [aircraftRankings, setAircraftRankings] = useState([]);
  const [fleetScores, setFleetScores] = useState([]);
  const [componentClusters, setComponentClusters] = useState([]);
  const [operatorMetrics, setOperatorMetrics] = useState([]);
  const [selectedAircraft, setSelectedAircraft] = useState(null);
  const [aircraftRiskData, setAircraftRiskData] = useState(null);
  const [predictionData, setPredictionData] = useState(null);
  const [fleetMetrics, setFleetMetrics] = useState(null);
  const [fleetTrends, setFleetTrends] = useState(null);
  const [componentTrends, setComponentTrends] = useState(null);
  const [lifeLimitAlerts, setLifeLimitAlerts] = useState(null);
  
  // Filter state
  const [filters, setFilters] = useState({
    aircraftId: '',
    fleetGroup: '',
    operatorId: ''
  });
  const [filtersApplied, setFiltersApplied] = useState(false);
  
  // Filter options data
  const [aircraftList, setAircraftList] = useState([]);
  const [fleetGroups, setFleetGroups] = useState([]);
  const [operators, setOperators] = useState([]);

  // Fetch filter options
  const fetchFilterOptions = useCallback(async () => {
    try {
      // Fetch aircraft list
      const acResponse = await getAircraftDropdown(api);
      setAircraftList(acResponse.data || []);
      
      // Extract unique fleet groups
      const groups = [...new Set((acResponse.data || []).map(a => a.fleet_group).filter(Boolean))];
      setFleetGroups(groups);
      
      // Fetch operators
      const opResponse = await getOrganizationsList(api, { organization_type: 'operator' });
      setOperators(opResponse.data || []);
    } catch (error) {
      console.error('Failed to fetch filter options:', error);
    }
  }, [api]);

  useEffect(() => {
    fetchFilterOptions();
  }, [fetchFilterOptions]);

  useEffect(() => {
    fetchAllData();
  }, [api, filters, filtersApplied]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchDashboard(),
        fetchAircraftRankings(),
        fetchFleetScores(),
        fetchComponentClusters(),
        fetchOperatorMetrics(),
        fetchFleetMetrics(),
        fetchFleetTrends(),
        fetchComponentTrends(),
        fetchLifeLimitAlerts()
      ]);
    } catch (error) {
      toast.error('Failed to fetch analytics data');
    } finally {
      setLoading(false);
    }
  };

  const fetchDashboard = async () => {
    try {
      const response = await api.get('/asset-analytics/dashboard/summary');
      setDashboardData(response.data);
    } catch (error) {
      console.error('Dashboard fetch error:', error);
    }
  };

  const fetchAircraftRankings = async () => {
    try {
      const params = new URLSearchParams({ limit: '20' });
      if (filters.operatorId) params.append('operator_id', filters.operatorId);
      if (filters.fleetGroup) params.append('fleet_group', filters.fleetGroup);
      
      const response = await api.get(`/asset-analytics/aircraft/risk-rankings?${params}`);
      let rankings = response.data.rankings || [];
      
      // If specific aircraft selected, filter to just that one (or put it first)
      if (filters.aircraftId) {
        const selectedAc = rankings.find(r => r.aircraft_id === filters.aircraftId);
        if (selectedAc) {
          rankings = [selectedAc, ...rankings.filter(r => r.aircraft_id !== filters.aircraftId)];
        }
      }
      
      setAircraftRankings(rankings);
    } catch (error) {
      console.error('Rankings fetch error:', error);
    }
  };

  const fetchFleetScores = async () => {
    try {
      const response = await api.get('/asset-analytics/fleet/risk-scores');
      setFleetScores(response.data.fleet_scores || []);
    } catch (error) {
      console.error('Fleet scores fetch error:', error);
    }
  };

  const fetchComponentClusters = async () => {
    try {
      const response = await api.get('/asset-analytics/components/hazard-clusters');
      setComponentClusters(response.data.clusters || []);
    } catch (error) {
      console.error('Component clusters fetch error:', error);
    }
  };

  const fetchOperatorMetrics = async () => {
    try {
      const response = await api.get('/asset-analytics/operators/safety-metrics');
      setOperatorMetrics(response.data.operators || []);
    } catch (error) {
      console.error('Operator metrics fetch error:', error);
    }
  };

  const fetchFleetMetrics = async () => {
    try {
      const params = new URLSearchParams({ days: '365' });
      if (filters.fleetGroup) params.append('fleet_group', filters.fleetGroup);
      if (filters.operatorId) params.append('operator_id', filters.operatorId);
      
      const response = await api.get(`/asset-analytics/fleet/safety-metrics?${params}`);
      setFleetMetrics(response.data);
    } catch (error) {
      console.error('Fleet metrics fetch error:', error);
    }
  };

  const fetchFleetTrends = async () => {
    try {
      const params = new URLSearchParams({ months: '12' });
      if (filters.fleetGroup) params.append('fleet_group', filters.fleetGroup);
      
      const response = await api.get(`/asset-analytics/fleet/trends?${params}`);
      setFleetTrends(response.data);
    } catch (error) {
      console.error('Fleet trends fetch error:', error);
    }
  };

  const fetchComponentTrends = async () => {
    try {
      const response = await api.get('/asset-analytics/components/failure-trends?months=12');
      setComponentTrends(response.data);
    } catch (error) {
      console.error('Component trends fetch error:', error);
    }
  };

  const fetchLifeLimitAlerts = async () => {
    try {
      const response = await api.get('/asset-analytics/components/life-limit-alerts?threshold_percent=90');
      setLifeLimitAlerts(response.data);
    } catch (error) {
      console.error('Life limit alerts fetch error:', error);
    }
  };

  const fetchAircraftRiskScore = async (aircraftId) => {
    try {
      const response = await api.get(`/asset-analytics/aircraft/${aircraftId}/risk-score`);
      setAircraftRiskData(response.data);
      setSelectedAircraft(aircraftId);
    } catch (error) {
      toast.error('Failed to fetch aircraft risk data');
    }
  };

  const fetchPrediction = async (aircraftId) => {
    try {
      const response = await api.get(`/asset-analytics/predict/aircraft/${aircraftId}`);
      setPredictionData(response.data);
    } catch (error) {
      toast.error('Failed to fetch prediction');
    }
  };

  const getRiskBadgeClass = (level) => {
    const classes = {
      critical: 'bg-red-500/20 text-red-400 border-red-500/30',
      high: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
      medium: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      low: 'bg-green-500/20 text-green-400 border-green-500/30',
      acceptable: 'bg-blue-500/20 text-blue-400 border-blue-500/30'
    };
    return classes[level] || classes.medium;
  };

  const TrendIcon = ({ trend }) => {
    if (trend === 'increasing') return <TrendingUp className="w-4 h-4 text-red-500" />;
    if (trend === 'decreasing') return <TrendingDown className="w-4 h-4 text-green-500" />;
    return <Minus className="w-4 h-4 text-gray-500" />;
  };

  const clearFilters = () => {
    setFilters({ aircraftId: '', fleetGroup: '', operatorId: '' });
    setFiltersApplied(false);
  };

  const applyFilters = () => {
    setFiltersApplied(true);
    fetchAllData();
  };

  const hasActiveFilters = filters.aircraftId || filters.fleetGroup || filters.operatorId;

  if (loading && !filtersApplied) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-sky-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className={cn("text-2xl font-bold", isDark ? "text-white" : "text-gray-900")}>
            Asset Safety Analytics
          </h1>
          <p className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-500")}>
            Predictive risk analysis, fleet scoring, and component hazard clustering
          </p>
        </div>
        <Button onClick={fetchAllData} variant="outline" className="gap-2">
          <RefreshCw className={cn("w-4 h-4", loading && "animate-spin")} />
          Refresh
        </Button>
      </div>

      {/* Filters Panel */}
      <Card className={cn(isDark ? "bg-slate-900/50 border-slate-800" : "bg-white")}>
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <Filter className="w-4 h-4 text-sky-500" />
            <span className={cn("font-medium text-sm", isDark ? "text-white" : "text-gray-900")}>
              Filter Analytics
            </span>
            {hasActiveFilters && (
              <Badge className="bg-sky-500/20 text-sky-400 text-xs ml-2">
                Filters Active
              </Badge>
            )}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
            {/* Aircraft Filter */}
            <div>
              <Label className={cn("text-xs mb-1.5 block", isDark ? "text-slate-400" : "text-gray-500")}>
                Aircraft Registration
              </Label>
              <Select 
                value={filters.aircraftId || 'all'} 
                onValueChange={(v) => setFilters({...filters, aircraftId: v === 'all' ? '' : v})}
              >
                <SelectTrigger className={cn("w-full", isDark ? "bg-slate-950 border-slate-800" : "")}>
                  <div className="flex items-center gap-2">
                    <Plane className="w-4 h-4 text-sky-500" />
                    <SelectValue placeholder="All Aircraft" />
                  </div>
                </SelectTrigger>
                <SelectContent className={cn(isDark ? "bg-slate-900 border-slate-800" : "")}>
                  <SelectItem value="all">All Aircraft</SelectItem>
                  {aircraftList.map(ac => (
                    <SelectItem key={ac.id} value={ac.id}>
                      <span className="font-mono">{ac.registration}</span>
                      <span className="text-xs text-slate-500 ml-2">{ac.aircraft_type}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Fleet Group Filter */}
            <div>
              <Label className={cn("text-xs mb-1.5 block", isDark ? "text-slate-400" : "text-gray-500")}>
                Fleet Group
              </Label>
              <Select 
                value={filters.fleetGroup || 'all'} 
                onValueChange={(v) => setFilters({...filters, fleetGroup: v === 'all' ? '' : v})}
              >
                <SelectTrigger className={cn("w-full", isDark ? "bg-slate-950 border-slate-800" : "")}>
                  <div className="flex items-center gap-2">
                    <Layers className="w-4 h-4 text-orange-500" />
                    <SelectValue placeholder="All Fleets" />
                  </div>
                </SelectTrigger>
                <SelectContent className={cn(isDark ? "bg-slate-900 border-slate-800" : "")}>
                  <SelectItem value="all">All Fleets</SelectItem>
                  {fleetGroups.map(fg => (
                    <SelectItem key={fg} value={fg}>{fg}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Operator Filter */}
            <div>
              <Label className={cn("text-xs mb-1.5 block", isDark ? "text-slate-400" : "text-gray-500")}>
                Operator
              </Label>
              <Select 
                value={filters.operatorId || 'all'} 
                onValueChange={(v) => setFilters({...filters, operatorId: v === 'all' ? '' : v})}
              >
                <SelectTrigger className={cn("w-full", isDark ? "bg-slate-950 border-slate-800" : "")}>
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-purple-500" />
                    <SelectValue placeholder="All Operators" />
                  </div>
                </SelectTrigger>
                <SelectContent className={cn(isDark ? "bg-slate-900 border-slate-800" : "")}>
                  <SelectItem value="all">All Operators</SelectItem>
                  {operators.map(op => (
                    <SelectItem key={op.id} value={op.id}>{op.legal_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Filter Actions */}
            <div className="flex gap-2">
              <Button onClick={applyFilters} className="bg-sky-600 hover:bg-sky-700 flex-1">
                Apply Filters
              </Button>
              {hasActiveFilters && (
                <Button onClick={clearFilters} variant="outline" className="border-slate-700">
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>

          {/* Active Filters Display */}
          {hasActiveFilters && (
            <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-slate-700">
              {filters.aircraftId && (
                <Badge className="bg-sky-500/20 text-sky-400 border border-sky-500/30">
                  <Plane className="w-3 h-3 mr-1" />
                  {aircraftList.find(a => a.id === filters.aircraftId)?.registration}
                  <X 
                    className="w-3 h-3 ml-1 cursor-pointer" 
                    onClick={() => setFilters({...filters, aircraftId: ''})}
                  />
                </Badge>
              )}
              {filters.fleetGroup && (
                <Badge className="bg-orange-500/20 text-orange-400 border border-orange-500/30">
                  <Layers className="w-3 h-3 mr-1" />
                  {filters.fleetGroup}
                  <X 
                    className="w-3 h-3 ml-1 cursor-pointer" 
                    onClick={() => setFilters({...filters, fleetGroup: ''})}
                  />
                </Badge>
              )}
              {filters.operatorId && (
                <Badge className="bg-purple-500/20 text-purple-400 border border-purple-500/30">
                  <Building2 className="w-3 h-3 mr-1" />
                  {operators.find(o => o.id === filters.operatorId)?.legal_name}
                  <X 
                    className="w-3 h-3 ml-1 cursor-pointer" 
                    onClick={() => setFilters({...filters, operatorId: ''})}
                  />
                </Badge>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dashboard Summary Cards */}
      {dashboardData && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className={cn(isDark ? "bg-slate-900/50 border-slate-800" : "bg-white")}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-sky-500/20">
                  <Plane className="w-5 h-5 text-sky-500" />
                </div>
                <div>
                  <div className={cn("text-2xl font-bold", isDark ? "text-white" : "text-gray-900")}>
                    {dashboardData.total_active_aircraft}
                  </div>
                  <div className={cn("text-xs", isDark ? "text-slate-400" : "text-gray-500")}>
                    Active Aircraft
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className={cn(isDark ? "bg-slate-900/50 border-slate-800" : "bg-white")}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-orange-500/20">
                  <AlertTriangle className="w-5 h-5 text-orange-500" />
                </div>
                <div>
                  <div className={cn("text-2xl font-bold", isDark ? "text-white" : "text-gray-900")}>
                    {dashboardData.aircraft_with_incidents_90d}
                  </div>
                  <div className={cn("text-xs", isDark ? "text-slate-400" : "text-gray-500")}>
                    With Incidents (90d)
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className={cn(isDark ? "bg-slate-900/50 border-slate-800" : "bg-white")}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-red-500/20">
                  <Shield className="w-5 h-5 text-red-500" />
                </div>
                <div>
                  <div className={cn("text-2xl font-bold", isDark ? "text-white" : "text-gray-900")}>
                    {dashboardData.high_risk_aircraft}
                  </div>
                  <div className={cn("text-xs", isDark ? "text-slate-400" : "text-gray-500")}>
                    High Risk Aircraft
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className={cn(isDark ? "bg-slate-900/50 border-slate-800" : "bg-white")}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-500/20">
                  <Target className="w-5 h-5 text-green-500" />
                </div>
                <div>
                  <div className={cn("text-2xl font-bold", isDark ? "text-white" : "text-gray-900")}>
                    {dashboardData.incident_free_rate}%
                  </div>
                  <div className={cn("text-xs", isDark ? "text-slate-400" : "text-gray-500")}>
                    Incident-Free Rate
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Tabs */}
      <Tabs defaultValue="aircraft" className="w-full">
        <TabsList className={cn("grid w-full grid-cols-5", isDark ? "bg-slate-800" : "")}>
          <TabsTrigger value="aircraft">Aircraft Risk</TabsTrigger>
          <TabsTrigger value="fleet">Fleet Scores</TabsTrigger>
          <TabsTrigger value="metrics">Fleet Metrics</TabsTrigger>
          <TabsTrigger value="components">Component Clusters</TabsTrigger>
          <TabsTrigger value="operators">Operator Metrics</TabsTrigger>
        </TabsList>

        {/* Aircraft Risk Rankings */}
        <TabsContent value="aircraft" className="space-y-4">
          <Card className={cn(isDark ? "bg-slate-900/50 border-slate-800" : "bg-white")}>
            <CardHeader>
              <CardTitle className={isDark ? "text-white" : ""}>Aircraft Risk Rankings</CardTitle>
              <CardDescription>Top aircraft by risk score based on incident history</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {aircraftRankings.map((ac, idx) => (
                  <div 
                    key={ac.aircraft_id}
                    className={cn(
                      "flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors",
                      isDark ? "bg-slate-800 hover:bg-slate-700" : "bg-gray-50 hover:bg-gray-100"
                    )}
                    onClick={() => {
                      fetchAircraftRiskScore(ac.aircraft_id);
                      fetchPrediction(ac.aircraft_id);
                    }}
                  >
                    <div className="flex items-center gap-4">
                      <div className={cn(
                        "w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm",
                        idx === 0 ? "bg-red-500/20 text-red-500" :
                        idx === 1 ? "bg-orange-500/20 text-orange-500" :
                        idx === 2 ? "bg-yellow-500/20 text-yellow-500" :
                        "bg-slate-500/20 text-slate-500"
                      )}>
                        {idx + 1}
                      </div>
                      <div>
                        <div className={cn("font-mono font-bold", isDark ? "text-white" : "")}>
                          {ac.registration}
                        </div>
                        <div className={cn("text-xs", isDark ? "text-slate-400" : "text-gray-500")}>
                          {ac.aircraft_type} • {ac.operator || 'No operator'}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <div className={cn("text-sm", isDark ? "text-slate-300" : "")}>
                          {ac.incident_count} incidents
                        </div>
                        <div className={cn("text-xs", isDark ? "text-slate-400" : "text-gray-500")}>
                          {ac.open_risks} open risks
                        </div>
                      </div>
                      <div className={cn(
                        "w-16 text-center py-1 rounded font-bold text-sm",
                        ac.risk_score >= 50 ? "bg-red-500/20 text-red-500" :
                        ac.risk_score >= 25 ? "bg-orange-500/20 text-orange-500" :
                        "bg-green-500/20 text-green-500"
                      )}>
                        {ac.risk_score}
                      </div>
                    </div>
                  </div>
                ))}
                {aircraftRankings.length === 0 && (
                  <div className={cn("text-center py-8", isDark ? "text-slate-400" : "text-gray-500")}>
                    No aircraft data available
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Selected Aircraft Details */}
          {aircraftRiskData && (
            <Card className={cn(isDark ? "bg-slate-900/50 border-slate-800" : "bg-white")}>
              <CardHeader>
                <CardTitle className={cn("flex items-center gap-2", isDark ? "text-white" : "")}>
                  <Plane className="w-5 h-5 text-sky-500" />
                  {aircraftRiskData.registration} - Detailed Risk Analysis
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className={cn("p-4 rounded-lg", isDark ? "bg-slate-800" : "bg-gray-50")}>
                    <div className={cn("text-sm mb-2", isDark ? "text-slate-400" : "text-gray-500")}>
                      Overall Risk Score
                    </div>
                    <div className="flex items-center gap-3">
                      <div className={cn("text-4xl font-bold", isDark ? "text-white" : "")}>
                        {aircraftRiskData.risk_score}
                      </div>
                      <Badge className={getRiskBadgeClass(aircraftRiskData.risk_category)}>
                        {aircraftRiskData.risk_category}
                      </Badge>
                      <TrendIcon trend={aircraftRiskData.trend} />
                    </div>
                  </div>
                  
                  <div className={cn("p-4 rounded-lg", isDark ? "bg-slate-800" : "bg-gray-50")}>
                    <div className={cn("text-sm mb-2", isDark ? "text-slate-400" : "text-gray-500")}>
                      Score Components
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>Incidents</span>
                        <span>{aircraftRiskData.components?.incident_score}</span>
                      </div>
                      <Progress value={aircraftRiskData.components?.incident_score} className="h-2" />
                      <div className="flex justify-between text-sm">
                        <span>Open Risks</span>
                        <span>{aircraftRiskData.components?.risk_score}</span>
                      </div>
                      <Progress value={aircraftRiskData.components?.risk_score} className="h-2" />
                    </div>
                  </div>
                  
                  <div className={cn("p-4 rounded-lg", isDark ? "bg-slate-800" : "bg-gray-50")}>
                    <div className={cn("text-sm mb-2", isDark ? "text-slate-400" : "text-gray-500")}>
                      Trend Analysis
                    </div>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span>Recent 90 days:</span>
                        <span className="font-mono">{aircraftRiskData.recent_vs_previous?.recent_90_days}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Previous 90 days:</span>
                        <span className="font-mono">{aircraftRiskData.recent_vs_previous?.previous_90_days}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Prediction */}
                {predictionData && (
                  <div className={cn("mt-4 p-4 rounded-lg border", isDark ? "bg-slate-800/50 border-slate-700" : "bg-blue-50 border-blue-200")}>
                    <div className="flex items-center gap-2 mb-3">
                      <Cpu className="w-5 h-5 text-purple-500" />
                      <span className={cn("font-semibold", isDark ? "text-white" : "")}>
                        AI Predictive Analysis (90-day outlook)
                      </span>
                    </div>
                    <div className="grid md:grid-cols-3 gap-4">
                      <div>
                        <div className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-500")}>
                          Predicted Incidents
                        </div>
                        <div className={cn("text-2xl font-bold", isDark ? "text-white" : "")}>
                          {predictionData.prediction?.predicted_incidents_90d}
                        </div>
                      </div>
                      <div>
                        <div className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-500")}>
                          Risk Level
                        </div>
                        <Badge className={getRiskBadgeClass(predictionData.prediction?.risk_level)}>
                          {predictionData.prediction?.risk_level}
                        </Badge>
                      </div>
                      <div>
                        <div className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-500")}>
                          Trend Direction
                        </div>
                        <div className="flex items-center gap-2">
                          <TrendIcon trend={predictionData.trend_analysis?.trend_direction} />
                          <span className={isDark ? "text-white" : ""}>
                            {predictionData.trend_analysis?.trend_direction}
                          </span>
                        </div>
                      </div>
                    </div>
                    {predictionData.recommendations?.length > 0 && (
                      <div className={cn("mt-3 text-sm", isDark ? "text-slate-300" : "text-gray-700")}>
                        <strong>Recommendations:</strong>
                        <ul className="list-disc list-inside mt-1">
                          {predictionData.recommendations.map((rec, i) => (
                            <li key={i}>{rec}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Fleet Risk Scores */}
        <TabsContent value="fleet" className="space-y-4">
          <Card className={cn(isDark ? "bg-slate-900/50 border-slate-800" : "bg-white")}>
            <CardHeader>
              <CardTitle className={isDark ? "text-white" : ""}>Fleet Risk Scores</CardTitle>
              <CardDescription>Risk analysis aggregated by fleet group</CardDescription>
            </CardHeader>
            <CardContent>
              {fleetScores.length > 0 ? (
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={fleetScores}>
                      <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "#334155" : "#e5e7eb"} />
                      <XAxis dataKey="fleet_group" stroke={isDark ? "#94a3b8" : "#6b7280"} />
                      <YAxis stroke={isDark ? "#94a3b8" : "#6b7280"} />
                      <Tooltip 
                        contentStyle={{
                          backgroundColor: isDark ? "#1e293b" : "#ffffff",
                          border: `1px solid ${isDark ? "#334155" : "#e5e7eb"}`,
                          borderRadius: "8px"
                        }}
                      />
                      <Bar dataKey="risk_score" fill="#f97316" name="Risk Score" />
                      <Bar dataKey="total_incidents" fill="#3b82f6" name="Incidents" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className={cn("text-center py-8", isDark ? "text-slate-400" : "text-gray-500")}>
                  No fleet data available
                </div>
              )}

              <div className="mt-4 space-y-2">
                {fleetScores.map(fleet => (
                  <div 
                    key={fleet.fleet_group}
                    className={cn("flex items-center justify-between p-3 rounded-lg", isDark ? "bg-slate-800" : "bg-gray-50")}
                  >
                    <div>
                      <div className={cn("font-semibold", isDark ? "text-white" : "")}>
                        {fleet.fleet_group}
                      </div>
                      <div className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-500")}>
                        {fleet.aircraft_count} aircraft • {fleet.incidents_per_aircraft} incidents/aircraft
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <div className={cn("text-sm", isDark ? "text-slate-300" : "")}>
                          {fleet.total_incidents} incidents
                        </div>
                        <div className={cn("text-xs", isDark ? "text-slate-400" : "text-gray-500")}>
                          {fleet.open_risks} open risks
                        </div>
                      </div>
                      <Badge className={
                        fleet.risk_score >= 50 ? "bg-red-500/20 text-red-400" :
                        fleet.risk_score >= 25 ? "bg-orange-500/20 text-orange-400" :
                        "bg-green-500/20 text-green-400"
                      }>
                        Score: {fleet.risk_score}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Fleet Safety Metrics (Phase 2) */}
        <TabsContent value="metrics" className="space-y-4">
          {/* KPI Summary Cards */}
          {fleetMetrics && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className={cn(isDark ? "bg-slate-900/50 border-slate-800" : "bg-white")}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-sky-500/20">
                      <Activity className="w-5 h-5 text-sky-500" />
                    </div>
                    <div>
                      <div className={cn("text-xl font-bold", isDark ? "text-white" : "text-gray-900")}>
                        {fleetMetrics.fleet_incident_rate_per_1000_fh?.toFixed(2)}
                      </div>
                      <div className={cn("text-xs", isDark ? "text-slate-400" : "text-gray-500")}>
                        Incidents/1000 FH
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className={cn(isDark ? "bg-slate-900/50 border-slate-800" : "bg-white")}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-purple-500/20">
                      <Target className="w-5 h-5 text-purple-500" />
                    </div>
                    <div>
                      <div className={cn("text-xl font-bold", isDark ? "text-white" : "text-gray-900")}>
                        {fleetMetrics.total_incidents}
                      </div>
                      <div className={cn("text-xs", isDark ? "text-slate-400" : "text-gray-500")}>
                        Total Incidents (1yr)
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className={cn(isDark ? "bg-slate-900/50 border-slate-800" : "bg-white")}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-green-500/20">
                      <Shield className="w-5 h-5 text-green-500" />
                    </div>
                    <div>
                      <div className={cn("text-xl font-bold", isDark ? "text-white" : "text-gray-900")}>
                        {fleetMetrics.kpi_summary?.incident_free_aircraft}
                      </div>
                      <div className={cn("text-xs", isDark ? "text-slate-400" : "text-gray-500")}>
                        Incident-Free Aircraft
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className={cn(isDark ? "bg-slate-900/50 border-slate-800" : "bg-white")}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-red-500/20">
                      <AlertTriangle className="w-5 h-5 text-red-500" />
                    </div>
                    <div>
                      <div className={cn("text-xl font-bold", isDark ? "text-white" : "text-gray-900")}>
                        {fleetMetrics.kpi_summary?.high_rate_aircraft}
                      </div>
                      <div className={cn("text-xs", isDark ? "text-slate-400" : "text-gray-500")}>
                        High-Rate Aircraft
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Trends Chart */}
          {fleetTrends && fleetTrends.monthly_data?.length > 0 && (
            <Card className={cn(isDark ? "bg-slate-900/50 border-slate-800" : "bg-white")}>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle className={isDark ? "text-white" : ""}>Incident Trend (12 Months)</CardTitle>
                    <CardDescription>Monthly incident count with trend analysis</CardDescription>
                  </div>
                  <Badge className={
                    fleetTrends.trend_direction === 'increasing' ? "bg-red-500/20 text-red-400" :
                    fleetTrends.trend_direction === 'decreasing' ? "bg-green-500/20 text-green-400" :
                    "bg-blue-500/20 text-blue-400"
                  }>
                    <TrendIcon trend={fleetTrends.trend_direction} />
                    <span className="ml-1 capitalize">{fleetTrends.trend_direction}</span>
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="h-[250px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={fleetTrends.monthly_data}>
                      <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "#334155" : "#e5e7eb"} />
                      <XAxis dataKey="month_label" stroke={isDark ? "#94a3b8" : "#6b7280"} />
                      <YAxis stroke={isDark ? "#94a3b8" : "#6b7280"} />
                      <Tooltip 
                        contentStyle={{
                          backgroundColor: isDark ? "#1e293b" : "#ffffff",
                          border: `1px solid ${isDark ? "#334155" : "#e5e7eb"}`,
                          borderRadius: "8px"
                        }}
                      />
                      <Line type="monotone" dataKey="incidents" stroke="#3b82f6" strokeWidth={2} dot={{ r: 4 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Aircraft Rates Table */}
          {fleetMetrics && fleetMetrics.aircraft_metrics?.length > 0 && (
            <Card className={cn(isDark ? "bg-slate-900/50 border-slate-800" : "bg-white")}>
              <CardHeader>
                <CardTitle className={isDark ? "text-white" : ""}>Aircraft Incident Rates</CardTitle>
                <CardDescription>Rates normalized per 1,000 flight hours</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {fleetMetrics.aircraft_metrics.map((ac, idx) => (
                    <div 
                      key={ac.registration}
                      className={cn(
                        "flex items-center justify-between p-3 rounded-lg",
                        isDark ? "bg-slate-800" : "bg-gray-50",
                        ac.rate_per_1000_fh > fleetMetrics.fleet_incident_rate_per_1000_fh * 1.5 && "border-l-4 border-red-500"
                      )}
                    >
                      <div className="flex items-center gap-4">
                        <Plane className="w-5 h-5 text-sky-500" />
                        <div>
                          <div className={cn("font-mono font-bold", isDark ? "text-white" : "")}>
                            {ac.registration}
                          </div>
                          <div className={cn("text-xs", isDark ? "text-slate-400" : "text-gray-500")}>
                            {ac.aircraft_type} • {ac.flight_hours?.toLocaleString()} FH
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-6">
                        <div className="text-right">
                          <div className={cn("text-sm font-medium", isDark ? "text-white" : "")}>
                            {ac.incident_count} incidents
                          </div>
                          <div className={cn("text-xs", isDark ? "text-slate-400" : "text-gray-500")}>
                            {ac.cycles?.toLocaleString()} cycles
                          </div>
                        </div>
                        <div className={cn(
                          "w-24 text-center py-1 px-2 rounded font-bold text-sm",
                          ac.rate_per_1000_fh > fleetMetrics.fleet_incident_rate_per_1000_fh * 1.5 
                            ? "bg-red-500/20 text-red-500" 
                            : ac.rate_per_1000_fh > fleetMetrics.fleet_incident_rate_per_1000_fh
                            ? "bg-orange-500/20 text-orange-500"
                            : "bg-green-500/20 text-green-500"
                        )}>
                          {ac.rate_per_1000_fh?.toFixed(2)}/kFH
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Component Failure Trends */}
          {componentTrends && componentTrends.categories?.length > 0 && (
            <Card className={cn(isDark ? "bg-slate-900/50 border-slate-800" : "bg-white")}>
              <CardHeader>
                <CardTitle className={isDark ? "text-white" : ""}>Component Failure Trends</CardTitle>
                <CardDescription>
                  Top failing component: {componentTrends.top_failing_category || 'N/A'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[200px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={componentTrends.categories.slice(0, 8)} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "#334155" : "#e5e7eb"} />
                      <XAxis type="number" stroke={isDark ? "#94a3b8" : "#6b7280"} />
                      <YAxis dataKey="category" type="category" width={100} stroke={isDark ? "#94a3b8" : "#6b7280"} />
                      <Tooltip 
                        contentStyle={{
                          backgroundColor: isDark ? "#1e293b" : "#ffffff",
                          border: `1px solid ${isDark ? "#334155" : "#e5e7eb"}`,
                          borderRadius: "8px"
                        }}
                      />
                      <Bar dataKey="total_incidents" fill="#f97316" name="Incidents" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Component Hazard Clusters */}
        <TabsContent value="components" className="space-y-4">
          <Card className={cn(isDark ? "bg-slate-900/50 border-slate-800" : "bg-white")}>
            <CardHeader>
              <CardTitle className={isDark ? "text-white" : ""}>Component Hazard Clustering</CardTitle>
              <CardDescription>Incidents and risks grouped by component category</CardDescription>
            </CardHeader>
            <CardContent>
              {componentClusters.length > 0 ? (
                <>
                  <div className="h-[250px] mb-4">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={componentClusters.slice(0, 10)} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "#334155" : "#e5e7eb"} />
                        <XAxis type="number" stroke={isDark ? "#94a3b8" : "#6b7280"} />
                        <YAxis dataKey="category" type="category" width={100} stroke={isDark ? "#94a3b8" : "#6b7280"} />
                        <Tooltip 
                          contentStyle={{
                            backgroundColor: isDark ? "#1e293b" : "#ffffff",
                            border: `1px solid ${isDark ? "#334155" : "#e5e7eb"}`,
                            borderRadius: "8px"
                          }}
                        />
                        <Bar dataKey="hazard_score" fill="#ef4444" name="Hazard Score" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    {componentClusters.slice(0, 6).map(cluster => (
                      <div 
                        key={cluster.category}
                        className={cn("p-4 rounded-lg", isDark ? "bg-slate-800" : "bg-gray-50")}
                      >
                        <div className="flex justify-between items-start mb-2">
                          <div className={cn("font-semibold capitalize", isDark ? "text-white" : "")}>
                            {cluster.category?.replace(/_/g, ' ')}
                          </div>
                          <Badge className={
                            cluster.hazard_score >= 100 ? "bg-red-500/20 text-red-400" :
                            cluster.hazard_score >= 50 ? "bg-orange-500/20 text-orange-400" :
                            "bg-green-500/20 text-green-400"
                          }>
                            Score: {cluster.hazard_score}
                          </Badge>
                        </div>
                        <div className={cn("text-sm space-y-1", isDark ? "text-slate-400" : "text-gray-500")}>
                          <div>Incidents: {cluster.incident_count}</div>
                          <div>Open Risks: {cluster.risk_count}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className={cn("text-center py-8", isDark ? "text-slate-400" : "text-gray-500")}>
                  No component hazard data available
                </div>
              )}
            </CardContent>
          </Card>

          {/* Life-Limit Alerts (Phase 3) */}
          {lifeLimitAlerts && lifeLimitAlerts.total_alerts > 0 && (
            <Card className={cn(isDark ? "bg-slate-900/50 border-slate-800" : "bg-white")}>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle className={cn("flex items-center gap-2", isDark ? "text-white" : "")}>
                      <AlertTriangle className="w-5 h-5 text-orange-500" />
                      Component Life-Limit Alerts
                    </CardTitle>
                    <CardDescription>Components approaching their life limits</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    {lifeLimitAlerts.critical_count > 0 && (
                      <Badge className="bg-red-500/20 text-red-400 border border-red-500/30">
                        {lifeLimitAlerts.critical_count} Critical
                      </Badge>
                    )}
                    {lifeLimitAlerts.warning_count > 0 && (
                      <Badge className="bg-orange-500/20 text-orange-400 border border-orange-500/30">
                        {lifeLimitAlerts.warning_count} Warning
                      </Badge>
                    )}
                    {lifeLimitAlerts.caution_count > 0 && (
                      <Badge className="bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">
                        {lifeLimitAlerts.caution_count} Caution
                      </Badge>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {lifeLimitAlerts.alerts.map((alert, idx) => (
                    <div 
                      key={alert.component_id}
                      className={cn(
                        "p-4 rounded-lg border-l-4",
                        isDark ? "bg-slate-800" : "bg-gray-50",
                        alert.overall_severity === "critical" ? "border-red-500" :
                        alert.overall_severity === "warning" ? "border-orange-500" :
                        "border-yellow-500"
                      )}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className={cn("font-mono font-bold", isDark ? "text-white" : "")}>
                              {alert.part_number}
                            </span>
                            <Badge className={cn(
                              "text-xs",
                              alert.overall_severity === "critical" ? "bg-red-500/20 text-red-400" :
                              alert.overall_severity === "warning" ? "bg-orange-500/20 text-orange-400" :
                              "bg-yellow-500/20 text-yellow-400"
                            )}>
                              {alert.overall_severity.toUpperCase()}
                            </Badge>
                          </div>
                          <div className={cn("text-sm mt-1", isDark ? "text-slate-400" : "text-gray-500")}>
                            S/N: {alert.serial_number} • {alert.description}
                          </div>
                          {alert.installed_aircraft && (
                            <div className={cn("text-xs mt-1", isDark ? "text-slate-500" : "text-gray-400")}>
                              <Plane className="w-3 h-3 inline mr-1" />
                              Installed on: {alert.installed_aircraft}
                            </div>
                          )}
                        </div>
                        <div className="text-right">
                          {alert.alerts.map((a, i) => (
                            <div key={i} className={cn("text-sm", isDark ? "text-slate-300" : "")}>
                              {a.type === "hours" ? (
                                <span>{a.remaining.toFixed(0)} hrs remaining ({a.remaining_percent}%)</span>
                              ) : (
                                <span>{a.remaining} cycles remaining ({a.remaining_percent}%)</span>
                              )}
                            </div>
                          ))}
                          {alert.related_incidents > 0 && (
                            <div className="text-xs text-red-400 mt-1">
                              ⚠️ {alert.related_incidents} related incident(s)
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Progress bars */}
                      <div className="mt-3 space-y-2">
                        {alert.alerts.map((a, i) => (
                          <div key={i}>
                            <div className="flex justify-between text-xs mb-1">
                              <span className={isDark ? "text-slate-400" : "text-gray-500"}>
                                {a.type === "hours" ? "Flight Hours" : "Cycles"}
                              </span>
                              <span className={isDark ? "text-slate-400" : "text-gray-500"}>
                                {a.current.toLocaleString()} / {a.limit.toLocaleString()}
                              </span>
                            </div>
                            <Progress 
                              value={100 - a.remaining_percent} 
                              className={cn(
                                "h-2",
                                a.severity === "critical" ? "[&>div]:bg-red-500" :
                                a.severity === "warning" ? "[&>div]:bg-orange-500" :
                                "[&>div]:bg-yellow-500"
                              )}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Operator Safety Metrics */}
        <TabsContent value="operators" className="space-y-4">
          <Card className={cn(isDark ? "bg-slate-900/50 border-slate-800" : "bg-white")}>
            <CardHeader>
              <CardTitle className={isDark ? "text-white" : ""}>Operator Safety Metrics</CardTitle>
              <CardDescription>Safety performance comparison across operators</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {operatorMetrics.map((op, idx) => (
                  <div 
                    key={op.organization_id}
                    className={cn("flex items-center justify-between p-4 rounded-lg", isDark ? "bg-slate-800" : "bg-gray-50")}
                  >
                    <div className="flex items-center gap-4">
                      <div className={cn(
                        "w-10 h-10 rounded-full flex items-center justify-center",
                        "bg-sky-500/20"
                      )}>
                        <Building2 className="w-5 h-5 text-sky-500" />
                      </div>
                      <div>
                        <div className={cn("font-semibold", isDark ? "text-white" : "")}>
                          {op.name}
                        </div>
                        <div className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-500")}>
                          {op.fleet_size} aircraft
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-6">
                      <div className="text-center">
                        <div className={cn("text-lg font-bold", isDark ? "text-white" : "")}>
                          {op.total_incidents}
                        </div>
                        <div className={cn("text-xs", isDark ? "text-slate-400" : "text-gray-500")}>
                          Incidents
                        </div>
                      </div>
                      <div className="text-center">
                        <div className={cn("text-lg font-bold", isDark ? "text-white" : "")}>
                          {op.incidents_per_aircraft}
                        </div>
                        <div className={cn("text-xs", isDark ? "text-slate-400" : "text-gray-500")}>
                          Per Aircraft
                        </div>
                      </div>
                      <div className="text-center">
                        <div className={cn("text-lg font-bold", isDark ? "text-white" : "")}>
                          {op.open_risks}
                        </div>
                        <div className={cn("text-xs", isDark ? "text-slate-400" : "text-gray-500")}>
                          Open Risks
                        </div>
                      </div>
                      <div className={cn(
                        "w-20 py-2 rounded text-center font-bold",
                        op.safety_score >= 80 ? "bg-green-500/20 text-green-400" :
                        op.safety_score >= 60 ? "bg-yellow-500/20 text-yellow-400" :
                        "bg-red-500/20 text-red-400"
                      )}>
                        {Math.round(op.safety_score)}
                      </div>
                    </div>
                  </div>
                ))}
                {operatorMetrics.length === 0 && (
                  <div className={cn("text-center py-8", isDark ? "text-slate-400" : "text-gray-500")}>
                    No operator data available
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
